import React, { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Sprout, Hammer, Recycle, Globe, TrendingUp, Microscope, Network } from 'lucide-react';
import { Card } from '../components/ui/Card';
import { AnimatedBackground } from '../components/AnimatedBackground';
export function Approach() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const {
    scrollYProgress
  } = useScroll({
    target: sectionRef,
    offset: ['start end', 'end start']
  });
  // Organic branching connections from central hub
  const centralToLeft = useTransform(scrollYProgress, [0.05, 0.2], [0, 1]);
  const centralToRight = useTransform(scrollYProgress, [0.08, 0.23], [0, 1]);
  const centralToBottom = useTransform(scrollYProgress, [0.12, 0.28], [0, 1]);
  const crossConnection = useTransform(scrollYProgress, [0.25, 0.35], [0, 1]);
  const bottomBranches = useTransform(scrollYProgress, [0.35, 0.5], [0, 1]);
  const networkOpacity = useTransform(scrollYProgress, [0.05, 0.2, 0.7, 0.9], [0, 1, 1, 0]);
  const themes = [{
    title: 'GROW',
    desc: 'Tech, tools, and infrastructure for regenerative agriculture and crop production.',
    icon: Sprout,
    color: '#78e1c4',
    delay: 0.1
  }, {
    title: 'USE',
    desc: 'Bio-based material innovation (e.g., packaging, textiles, etc.)',
    icon: Recycle,
    color: '#edcd99',
    delay: 0.2
  }, {
    title: 'BUILD',
    desc: 'Bio-based construction materials, climate-adaptive infrastructure.',
    icon: Hammer,
    color: '#a0e6e9',
    delay: 0.3
  }];
  return <div className="min-h-screen pt-20" data-id="element-269">
      {/* Hero Section */}
      <section className="relative h-[60vh] flex items-center justify-center overflow-hidden" data-id="element-270">
        <div className="absolute inset-0 bg-gradient-to-b from-[#0e2533] via-[#1c3f4d] to-[#0e2533] z-0" data-id="element-271" />
        <AnimatedBackground data-id="element-272" />
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1542273917363-3b1817f69a2d?auto=format&fit=crop&q=80')] bg-cover bg-center opacity-30 z-0" data-id="element-273" />
        <div className="absolute inset-0 bg-carbonBlack/40 z-10" data-id="element-274" />

        <div className="relative z-20 text-center px-4" data-id="element-275">
          <motion.h1 initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} className="text-5xl md:text-7xl font-bold text-white mb-4" data-id="element-276">
            OUR APPROACH
          </motion.h1>
          <motion.p initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.1
        }} className="text-2xl text-vitalityTeal font-light" data-id="element-277">
            A Biomimicry Lens
          </motion.p>
        </div>
      </section>

      {/* Mycelium Network Grid Section */}
      <section ref={sectionRef} className="py-32 bg-[#0e2533] relative overflow-hidden min-h-screen" data-id="element-278">
        {/* Animated Mycelium Network SVG - Organic Branching */}
        <motion.svg className="absolute inset-0 w-full h-full pointer-events-none z-0" style={{
        opacity: networkOpacity
      }} xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none" viewBox="0 0 1200 1600" data-id="element-279">
          <defs data-id="element-280">
            <linearGradient id="myceliumGrad" x1="0%" y1="0%" x2="100%" y2="100%" data-id="element-281">
              <stop offset="0%" stopColor="#78e1c4" stopOpacity="0.6" data-id="element-282" />
              <stop offset="50%" stopColor="#4DE7D4" stopOpacity="0.4" data-id="element-283" />
              <stop offset="100%" stopColor="#78e1c4" stopOpacity="0.2" data-id="element-284" />
            </linearGradient>
            <filter id="networkGlow" data-id="element-285">
              <feGaussianBlur stdDeviation="4" result="coloredBlur" data-id="element-286" />
              <feMerge data-id="element-287">
                <feMergeNode in="coloredBlur" data-id="element-288" />
                <feMergeNode in="SourceGraphic" data-id="element-289" />
              </feMerge>
            </filter>
          </defs>

          {/* Central Hub Node */}
          <motion.circle cx="600" cy="200" r="10" fill="#78e1c4" filter="url(#networkGlow)" animate={{
          scale: [1, 1.3, 1],
          opacity: [0.7, 1, 0.7]
        }} transition={{
          duration: 2,
          repeat: Infinity,
          ease: 'easeInOut'
        }} data-id="element-290" />

          {/* Organic branch to Nature-Inspired (left) */}
          <motion.path d="M 600,200 Q 500,350 350,550 T 250,750" stroke="url(#myceliumGrad)" strokeWidth="3" fill="none" filter="url(#networkGlow)" strokeLinecap="round" style={{
          pathLength: centralToLeft
        }} animate={{
          opacity: [0.6, 1, 0.6],
          strokeWidth: [3, 4, 3]
        }} transition={{
          duration: 3,
          repeat: Infinity,
          ease: 'easeInOut'
        }} data-id="element-291" />
          <motion.circle cx="250" cy="750" r="7" fill="#78e1c4" filter="url(#networkGlow)" animate={{
          scale: [1, 1.4, 1],
          opacity: [0.6, 1, 0.6]
        }} transition={{
          duration: 2.3,
          repeat: Infinity,
          ease: 'easeInOut',
          delay: 0.8
        }} data-id="element-292" />

          {/* Organic branch to Returns & Impact (right) */}
          <motion.path d="M 600,200 Q 700,350 850,550 T 950,750" stroke="url(#myceliumGrad)" strokeWidth="3" fill="none" filter="url(#networkGlow)" strokeLinecap="round" style={{
          pathLength: centralToRight
        }} animate={{
          opacity: [0.6, 1, 0.6],
          strokeWidth: [3, 4, 3]
        }} transition={{
          duration: 3.5,
          repeat: Infinity,
          ease: 'easeInOut',
          delay: 0.5
        }} data-id="element-293" />
          <motion.circle cx="950" cy="750" r="7" fill="#4DE7D4" filter="url(#networkGlow)" animate={{
          scale: [1, 1.4, 1],
          opacity: [0.6, 1, 0.6]
        }} transition={{
          duration: 2.7,
          repeat: Infinity,
          ease: 'easeInOut',
          delay: 1.2
        }} data-id="element-294" />

          {/* Organic branch to Geographic Focus (center-bottom) */}
          <motion.path d="M 600,200 Q 580,450 600,700 Q 620,900 600,1050" stroke="url(#myceliumGrad)" strokeWidth="3" fill="none" filter="url(#networkGlow)" strokeLinecap="round" style={{
          pathLength: centralToBottom
        }} animate={{
          opacity: [0.6, 1, 0.6],
          strokeWidth: [3, 4, 3]
        }} transition={{
          duration: 3.2,
          repeat: Infinity,
          ease: 'easeInOut',
          delay: 1
        }} data-id="element-295" />
          <motion.circle cx="600" cy="1050" r="8" fill="#78e1c4" filter="url(#networkGlow)" animate={{
          scale: [1, 1.3, 1],
          opacity: [0.7, 1, 0.7]
        }} transition={{
          duration: 2.2,
          repeat: Infinity,
          ease: 'easeInOut',
          delay: 1.5
        }} data-id="element-296" />

          {/* Secondary organic connections between feature nodes */}
          <motion.path d="M 250,750 Q 450,800 600,780 Q 750,800 950,750" stroke="url(#myceliumGrad)" strokeWidth="1.5" fill="none" strokeDasharray="5 5" strokeLinecap="round" style={{
          pathLength: crossConnection
        }} animate={{
          opacity: [0.2, 0.5, 0.2],
          strokeWidth: [1.5, 2, 1.5]
        }} transition={{
          duration: 4,
          repeat: Infinity,
          ease: 'easeInOut',
          delay: 1.5
        }} data-id="element-297" />

          {/* Subtle connecting threads from features to geographic focus */}
          <motion.path d="M 250,750 Q 350,900 600,1050" stroke="url(#myceliumGrad)" strokeWidth="1.5" fill="none" strokeDasharray="3 6" strokeLinecap="round" style={{
          pathLength: crossConnection
        }} animate={{
          opacity: [0.2, 0.4, 0.2],
          strokeWidth: [1.5, 2, 1.5]
        }} transition={{
          duration: 3.8,
          repeat: Infinity,
          ease: 'easeInOut',
          delay: 2
        }} data-id="element-298" />
          <motion.path d="M 950,750 Q 850,900 600,1050" stroke="url(#myceliumGrad)" strokeWidth="1.5" fill="none" strokeDasharray="3 6" strokeLinecap="round" style={{
          pathLength: crossConnection
        }} animate={{
          opacity: [0.2, 0.4, 0.2],
          strokeWidth: [1.5, 2, 1.5]
        }} transition={{
          duration: 4.2,
          repeat: Infinity,
          ease: 'easeInOut',
          delay: 2.3
        }} data-id="element-299" />

          {/* Branches from Geographic Focus to Investment Themes */}
          <motion.path d="M 600,1050 Q 450,1200 300,1350" stroke="url(#myceliumGrad)" strokeWidth="2.5" fill="none" strokeLinecap="round" style={{
          pathLength: bottomBranches
        }} animate={{
          opacity: [0.3, 0.6, 0.3],
          strokeWidth: [2.5, 3.5, 2.5]
        }} transition={{
          duration: 3.5,
          repeat: Infinity,
          ease: 'easeInOut',
          delay: 2.5
        }} data-id="element-300" />
          <motion.path d="M 600,1050 Q 600,1200 600,1350" stroke="url(#myceliumGrad)" strokeWidth="2.5" fill="none" strokeLinecap="round" style={{
          pathLength: bottomBranches
        }} animate={{
          opacity: [0.3, 0.6, 0.3],
          strokeWidth: [2.5, 3.5, 2.5]
        }} transition={{
          duration: 3.8,
          repeat: Infinity,
          ease: 'easeInOut',
          delay: 2.8
        }} data-id="element-301" />
          <motion.path d="M 600,1050 Q 750,1200 900,1350" stroke="url(#myceliumGrad)" strokeWidth="2.5" fill="none" strokeLinecap="round" style={{
          pathLength: bottomBranches
        }} animate={{
          opacity: [0.3, 0.6, 0.3],
          strokeWidth: [2.5, 3.5, 2.5]
        }} transition={{
          duration: 4,
          repeat: Infinity,
          ease: 'easeInOut',
          delay: 3
        }} data-id="element-302" />

          {/* Intermediate node markers along main branches */}
          <motion.circle cx="475" cy="375" r="4" fill="#4DE7D4" opacity="0.6" animate={{
          scale: [1, 1.5, 1],
          opacity: [0.4, 0.8, 0.4]
        }} transition={{
          duration: 2.5,
          repeat: Infinity,
          ease: 'easeInOut',
          delay: 0.3
        }} data-id="element-303" />
          <motion.circle cx="725" cy="375" r="4" fill="#4DE7D4" opacity="0.6" animate={{
          scale: [1, 1.5, 1],
          opacity: [0.4, 0.8, 0.4]
        }} transition={{
          duration: 2.8,
          repeat: Infinity,
          ease: 'easeInOut',
          delay: 0.6
        }} data-id="element-304" />
          <motion.circle cx="590" cy="450" r="4" fill="#78e1c4" opacity="0.6" animate={{
          scale: [1, 1.5, 1],
          opacity: [0.4, 0.8, 0.4]
        }} transition={{
          duration: 2.3,
          repeat: Infinity,
          ease: 'easeInOut',
          delay: 0.9
        }} data-id="element-305" />
        </motion.svg>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-306">
          {/* Central Hub Node - Main Intro */}
          <motion.div initial={{
          opacity: 0,
          scale: 0.9
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} className="max-w-3xl mx-auto text-center mb-24" data-id="element-307">
            <div className="relative" data-id="element-308">
              <div className="absolute -inset-4 bg-vitalityTeal/5 rounded-full blur-3xl" data-id="element-309" />
              <div className="relative bg-[#1c3f4d]/60 backdrop-blur-md border border-vitalityTeal/20 rounded-2xl p-12 shadow-2xl" data-id="element-310">
                <div className="flex items-center justify-center space-x-3 mb-6" data-id="element-311">
                  <Microscope className="w-6 h-6 text-vitalityTeal" data-id="element-312" />
                  <span className="text-vitalityTeal font-medium tracking-wider text-sm uppercase" data-id="element-313">
                    Core Methodology
                  </span>
                </div>
                <h2 className="text-4xl md:text-5xl font-bold text-white mb-6 leading-tight" data-id="element-314">
                  Nature's R&D + Modern Tech
                </h2>
                <p className="text-xl text-gray-300 leading-relaxed" data-id="element-315">
                  Vitality is a venture fund investing in deep tech and hard
                  tech solutions rooted in biomimicry, circular systems, and
                  resilience. We use a biomimicry-based filter to identify
                  startups that are creating essential solutions by applying 3.8
                  billion years of nature's R&D and modern advancements in tech
                  & AI.
                </p>
              </div>
            </div>
          </motion.div>

          {/* Key Message Node - Moved Above Feature Cards */}
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          delay: 0.2
        }} className="max-w-4xl mx-auto text-center mb-24" data-id="element-316">
            <div className="relative" data-id="element-317">
              <div className="absolute -inset-4 bg-cyan-500/5 rounded-full blur-3xl" data-id="element-318" />
              <div className="relative bg-gradient-to-br from-[#1c3f4d]/80 to-[#0e2533]/80 backdrop-blur-md border border-white/10 rounded-2xl p-10" data-id="element-319">
                <p className="text-2xl md:text-3xl font-medium text-white leading-relaxed" data-id="element-320">
                  "We believe solutions that tap into nature's intelligence will
                  be more
                  <span className="text-vitalityTeal" data-id="element-321"> energy-efficient</span>,
                  <span className="text-vitalityTeal" data-id="element-322"> resilient</span>,
                  <span className="text-vitalityTeal" data-id="element-323"> circular</span> and
                  <span className="text-vitalityTeal" data-id="element-324">
                    {' '}
                    cost effective
                  </span>{' '}
                  than other solutions in the market."
                </p>
              </div>
            </div>
          </motion.div>

          {/* Network Grid - Feature Nodes */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 mb-24 max-w-5xl mx-auto" data-id="element-325">
            {/* Nature-Inspired Node */}
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6,
            delay: 0.3
          }} className="relative" data-id="element-326">
              <div className="absolute -inset-2 bg-vitalityTeal/10 rounded-2xl blur-xl" data-id="element-327" />
              <Card className="relative h-full p-8 border-2 border-vitalityTeal/20" data-id="element-328">
                <div className="flex items-center space-x-4 mb-6" data-id="element-329">
                  <div className="w-14 h-14 rounded-full bg-vitalityTeal/20 flex items-center justify-center" data-id="element-330">
                    <Sprout className="w-7 h-7 text-vitalityTeal" data-id="element-331" />
                  </div>
                  <h3 className="text-2xl font-bold text-white" data-id="element-332">
                    Nature-Inspired
                  </h3>
                </div>
                <p className="text-gray-300 text-lg leading-relaxed mb-6" data-id="element-333">
                  Invest in nature-inspired startups across industries that
                  leverage biological principles for efficiency.
                </p>
              </Card>
            </motion.div>

            {/* Returns & Impact Node */}
            <motion.div initial={{
            opacity: 0,
            x: 30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6,
            delay: 0.4
          }} className="relative" data-id="element-334">
              <div className="absolute -inset-2 bg-vitalityTeal/10 rounded-2xl blur-xl" data-id="element-335" />
              <Card className="relative h-full p-8 border-2 border-vitalityTeal/20" data-id="element-336">
                <div className="flex items-center space-x-4 mb-6" data-id="element-337">
                  <div className="w-14 h-14 rounded-full bg-vitalityTeal/20 flex items-center justify-center" data-id="element-338">
                    <TrendingUp className="w-7 h-7 text-vitalityTeal" data-id="element-339" />
                  </div>
                  <h3 className="text-2xl font-bold text-white" data-id="element-340">
                    Returns & Impact
                  </h3>
                </div>
                <p className="text-gray-300 text-lg leading-relaxed mb-6" data-id="element-341">
                  Drive financial returns and tangible impact in biodiversity,
                  soil health, and water quality.
                </p>
              </Card>
            </motion.div>
          </div>

          {/* Geographic Focus Node */}
          <motion.div initial={{
          opacity: 0,
          scale: 0.9
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          delay: 0.5
        }} className="max-w-2xl mx-auto mb-32" data-id="element-342">
            <div className="relative" data-id="element-343">
              <div className="absolute -inset-3 bg-blue-500/10 rounded-2xl blur-2xl" data-id="element-344" />
              <Card className="relative p-10 border-2 border-vitalityTeal/20" data-id="element-345">
                <div className="flex items-center space-x-4 mb-6" data-id="element-346">
                  <div className="w-12 h-12 rounded-full bg-vitalityTeal/20 flex items-center justify-center" data-id="element-347">
                    <Globe className="w-6 h-6 text-vitalityTeal" data-id="element-348" />
                  </div>
                  <div data-id="element-349">
                    <span className="text-vitalityTeal font-medium tracking-wider text-xs uppercase block mb-1" data-id="element-350">
                      Focus Area
                    </span>
                    <h3 className="text-2xl font-bold text-white" data-id="element-351">
                      Stage and Geographic Focus
                    </h3>
                  </div>
                </div>
                <p className="text-xl text-gray-300 leading-relaxed mb-6" data-id="element-352">
                  Initially focusing on pre-seed and seed stage startups in
                  North America and the UK.
                </p>
                <div className="grid grid-cols-3 gap-4" data-id="element-353">
                  <div className="text-center p-3 bg-white/5 rounded-lg" data-id="element-354">
                    <div className="text-2xl font-bold text-vitalityTeal mb-1" data-id="element-355">
                      Pre-Seed
                    </div>
                    <div className="text-xs text-gray-400" data-id="element-356">Stage</div>
                  </div>
                  <div className="text-center p-3 bg-white/5 rounded-lg" data-id="element-357">
                    <div className="text-2xl font-bold text-vitalityTeal mb-1" data-id="element-358">
                      Seed
                    </div>
                    <div className="text-xs text-gray-400" data-id="element-359">Stage</div>
                  </div>
                  <div className="text-center p-3 bg-white/5 rounded-lg" data-id="element-360">
                    <div className="text-2xl font-bold text-vitalityTeal mb-1" data-id="element-361">
                      NA/UK
                    </div>
                    <div className="text-xs text-gray-400" data-id="element-362">Regions</div>
                  </div>
                </div>
              </Card>
            </div>
          </motion.div>

          {/* Investment Themes - Terminal Nodes with Honeycomb Layout */}
          <div className="text-center mb-12" data-id="element-363">
            <h2 className="text-4xl font-bold text-white mb-4" data-id="element-364">
              Investment Themes
            </h2>
            <p className="text-gray-400 text-lg" data-id="element-365">
              Three interconnected categories creating essential solutions
            </p>
          </div>

          <div className="relative max-w-5xl mx-auto" data-id="element-366">
            {/* Honeycomb grid layout */}
            <div className="flex flex-col md:flex-row items-center justify-center gap-8 md:gap-4" data-id="element-367">
              {themes.map((theme, index) => <motion.div key={theme.title} initial={{
              opacity: 0,
              scale: 0.8
            }} whileInView={{
              opacity: 1,
              scale: 1
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.6,
              delay: theme.delay
            }} className={`relative w-full md:w-80 ${index === 1 ? 'md:mt-16' : ''}`} data-id="element-368">
                  {/* Hexagonal glow */}
                  <div className="absolute -inset-3 blur-2xl opacity-40" style={{
                backgroundColor: `${theme.color}30`,
                clipPath: 'polygon(30% 0%, 70% 0%, 100% 50%, 70% 100%, 30% 100%, 0% 50%)'
              }} data-id="element-369" />

                  {/* Hexagonal container */}
                  <div className="relative bg-[#1c3f4d]/60 backdrop-blur-sm border-2 overflow-hidden" style={{
                clipPath: 'polygon(30% 0%, 70% 0%, 100% 50%, 70% 100%, 30% 100%, 0% 50%)',
                borderColor: `${theme.color}60`,
                minHeight: '380px'
              }} data-id="element-370">
                    {/* Honeycomb pattern overlay */}
                    <div className="absolute inset-0 opacity-10" style={{
                  backgroundImage: `radial-gradient(circle at center, ${theme.color} 1px, transparent 1px)`,
                  backgroundSize: '20px 20px'
                }} data-id="element-371" />

                    {/* Content */}
                    <div className="relative z-10 p-10 flex flex-col items-center text-center h-full justify-center" data-id="element-372">
                      {/* Icon in hexagonal frame */}
                      <div className="relative mb-6" data-id="element-373">
                        <div className="w-20 h-20 flex items-center justify-center" style={{
                      clipPath: 'polygon(30% 0%, 70% 0%, 100% 50%, 70% 100%, 30% 100%, 0% 50%)',
                      backgroundColor: `${theme.color}30`
                    }} data-id="element-374">
                          <theme.icon className="w-9 h-9" style={{
                        color: theme.color
                      }} data-id="element-375" />
                        </div>
                        {/* Pulsing ring */}
                        <motion.div className="absolute inset-0" style={{
                      clipPath: 'polygon(30% 0%, 70% 0%, 100% 50%, 70% 100%, 30% 100%, 0% 50%)',
                      border: `2px solid ${theme.color}`,
                      opacity: 0.5
                    }} animate={{
                      scale: [1, 1.2, 1],
                      opacity: [0.5, 0, 0.5]
                    }} transition={{
                      duration: 2.5,
                      repeat: Infinity,
                      ease: 'easeInOut',
                      delay: index * 0.3
                    }} data-id="element-376" />
                      </div>

                      <h3 className="text-2xl font-bold text-white mb-4 tracking-wide" data-id="element-377">
                        {theme.title}
                      </h3>

                      <p className="text-gray-300 leading-relaxed mb-6 text-sm" data-id="element-378">
                        {theme.desc}
                      </p>

                      {/* Status indicator */}
                      <div className="flex items-center space-x-2 text-xs" style={{
                    color: theme.color
                  }} data-id="element-379">
                        <div className="w-2 h-2 animate-pulse" style={{
                      backgroundColor: theme.color,
                      clipPath: 'polygon(50% 0%, 100% 50%, 50% 100%, 0% 50%)'
                    }} data-id="element-380" />
                      
                    
                      </div>
                    </div>
                  </div>

                  {/* Connecting lines between hexagons (visible on desktop) */}
                  {index < themes.length - 1 && <motion.div className="hidden md:block absolute top-1/2 -right-4 w-8 h-0.5 z-0" style={{
                backgroundColor: `${theme.color}40`
              }} initial={{
                scaleX: 0
              }} whileInView={{
                scaleX: 1
              }} viewport={{
                once: true
              }} transition={{
                duration: 0.8,
                delay: theme.delay + 0.3
              }} data-id="element-381" />}
                </motion.div>)}
            </div>

            {/* Decorative honeycomb cells in background */}
            <div className="absolute inset-0 pointer-events-none overflow-hidden opacity-5" data-id="element-382">
              {[...Array(6)].map((_, i) => <motion.div key={i} className="absolute w-24 h-24 border border-vitalityTeal" style={{
              clipPath: 'polygon(30% 0%, 70% 0%, 100% 50%, 70% 100%, 30% 100%, 0% 50%)',
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`
            }} animate={{
              rotate: [0, 360],
              scale: [1, 1.1, 1]
            }} transition={{
              duration: 20 + i * 5,
              repeat: Infinity,
              ease: 'linear'
            }} data-id="element-383" />)}
            </div>
          </div>
        </div>
      </section>
    </div>;
}